import { Text } from '@core/components/Text';
import { cn } from '@core/utils/styles';

interface LoadingSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  text?: string;
}

/**
 * A loading spinner component that displays an animated circular loading indicator.
 * Can optionally display text below the spinner.
 *
 * @example
 * ```tsx
 * // Basic spinner
 * <LoadingSpinner />
 *
 * // Spinner with text
 * <LoadingSpinner text="Loading data..." />
 *
 * // Customized spinner
 * <LoadingSpinner className="size-12 border-secondary" text="Please wait" />
 * ```
 */
export function LoadingSpinner({ className, text, ...props }: LoadingSpinnerProps) {
  if (text) {
    return (
      <div className="flex flex-col items-center gap-4">
        <Spinner className={className} {...props} />

        <Text size="sm" className="text-txt-dark">
          {text}
        </Text>
      </div>
    );
  }

  return <Spinner className={className} {...props} />;
}

function Spinner({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        ['size-8', 'border-primary rounded-full border-4 border-t-transparent', 'animate-spin'],
        className
      )}
      aria-label="Loading spinner"
      {...props}
    />
  );
}
